import React, { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { useCart } from '../context/CartContext';
import { CheckCircle, Loader2, AlertCircle, ShieldCheck } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const CheckoutSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const { clearCart } = useCart();
  const [status, setStatus] = useState('verifying'); // verifying, success, error
  const [orderId, setOrderId] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    const orderIdParam = searchParams.get('order_id');
    
    if (orderIdParam) {
      setOrderId(orderIdParam);
    }

    if (sessionId) {
      pollPaymentStatus(sessionId, 0);
    } else {
      setStatus('error');
      setErrorMessage('No payment session found');
    }
  }, [searchParams]);

  const pollPaymentStatus = async (sessionId, attempts) => {
    const maxAttempts = 15;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      setStatus('error');
      setErrorMessage('Payment verification timed out. Please contact support if you were charged.');
      return;
    }

    try {
      const response = await axios.get(`${API}/payments/status/${sessionId}`);
      const data = response.data;

      if (data.payment_status === 'paid') {
        setStatus('success');
        setOrderId(data.order_id);
        clearCart();
        return;
      } else if (data.status === 'expired' || data.payment_status === 'unpaid') {
        // Keep polling for a bit in case webhook hasn't processed yet
        if (attempts < 5) {
          setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
        } else {
          setStatus('error');
          setErrorMessage('Payment was not completed. Please try again or contact support.');
        }
        return;
      }

      // Continue polling
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
    } catch (error) {
      console.error('Error checking payment status:', error);
      if (attempts < maxAttempts - 1) {
        setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
      } else {
        setStatus('error');
        setErrorMessage('Error verifying payment. Please contact support.');
      }
    }
  };

  if (status === 'verifying') {
    return (
      <div className="min-h-screen bg-[#FAF8F5] flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-white rounded-2xl p-8 border border-[#E5E0D8] text-center">
          <Loader2 className="w-16 h-16 text-[#D4540A] mx-auto mb-6 animate-spin" />
          <h1 className="font-['Bodoni_Moda'] text-2xl font-bold text-[#2A2A2A] mb-2">
            Verifying Payment...
          </h1>
          <p className="text-[#666]">
            Please wait while we confirm your payment with Stripe.
          </p>
        </div>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="min-h-screen bg-[#FAF8F5] flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-white rounded-2xl p-8 border border-[#E5E0D8] text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <AlertCircle className="w-10 h-10 text-red-600" />
          </div>
          <h1 className="font-['Bodoni_Moda'] text-2xl font-bold text-[#2A2A2A] mb-2">
            Payment Issue
          </h1>
          <p className="text-[#666] mb-6">
            {errorMessage}
          </p>
          <div className="space-y-3">
            <Link to="/checkout">
              <Button className="w-full bg-[#D4540A] hover:bg-[#B84709] text-white py-4">
                Try Again
              </Button>
            </Link>
            <Link to="/contact">
              <Button variant="outline" className="w-full border-[#E5E0D8] py-4">
                Contact Support
              </Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAF8F5] flex items-center justify-center px-4" data-testid="checkout-success">
      <div className="max-w-md w-full bg-white rounded-2xl p-8 border border-[#E5E0D8] text-center">
        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
          <CheckCircle className="w-10 h-10 text-green-600" />
        </div>
        <h1 className="font-['Bodoni_Moda'] text-3xl font-bold text-[#2A2A2A] mb-2">
          Order Confirmed!
        </h1>
        <p className="text-[#666] mb-6">
          Thank you for your order. We'll get your blends shipped out soon.
        </p>
        <div className="bg-[#F5F3F0] rounded-lg p-4 mb-6">
          <p className="text-sm text-[#666]">Order ID</p>
          <p className="font-mono font-bold text-[#2A2A2A]" data-testid="order-id">{orderId}</p>
        </div>
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
          <div className="flex items-center justify-center mb-2">
            <ShieldCheck className="w-5 h-5 text-green-600 mr-2" />
            <span className="font-semibold text-green-800">Payment Confirmed</span>
          </div>
          <p className="text-sm text-green-700">
            Your payment has been processed successfully. You'll receive a confirmation email shortly.
          </p>
        </div>
        <Link to="/shop">
          <Button className="w-full bg-[#D4540A] hover:bg-[#B84709] text-white py-6" data-testid="continue-btn">
            Continue Shopping
          </Button>
        </Link>
        <Link to={`/track`} className="block mt-3">
          <Button variant="outline" className="w-full border-[#E5E0D8] py-4" data-testid="track-order-btn">
            Track Your Order
          </Button>
        </Link>
      </div>
    </div>
  );
};

export default CheckoutSuccessPage;
